import { lazy } from "react";

/**
 * Drop-in replacement for React.lazy that survives deploys.
 *
 * Problem this solves: a user loads the SPA at 10am — Vite-emitted bundle
 * URLs like /assets/Settings-aB1cD2.js are baked into the index chunk they
 * downloaded. We push a deploy at 11am that renames those chunks (different
 * hash, different filename). The old hash files are gone from the CDN. At
 * 12pm the user clicks a route they hadn't visited yet — the browser tries
 * to fetch /assets/Settings-aB1cD2.js, gets 404, and React.lazy throws:
 *   "Failed to fetch dynamically imported module"
 *
 * The ErrorBoundary recovers, but the next render asks for the same dead
 * URL again. Without intervention the page stays broken until the user
 * manually hard-refreshes (Cmd-Shift-R), which most users never do — they
 * just decide the product is broken and leave.
 *
 * Fix here:
 *   1. On the FIRST failure, set a sessionStorage flag and force a full
 *      page reload. The reload fetches a fresh index.html which references
 *      the new chunk hashes — problem solved.
 *   2. If the SECOND attempt also fails (post-reload), we're not facing a
 *      stale-bundle problem — it's a real network/server issue. Let the
 *      error propagate to ErrorBoundary so Sentry sees it.
 *
 * Usage is identical to React.lazy:
 *   const MyPage = lazyRetry(() => import("./pages/MyPage"));
 */
// Single session-wide reload budget. If a chunk fails, we reload once
// per session — not once per chunk — because in minified production
// builds many lazy imports share the same factory.toString() text
// and the previous per-key approach silently lost retries.
const RELOAD_FLAG_KEY = "__chunk_reloaded_this_session__";
const RELOAD_FLAG_TS_KEY = "__chunk_reloaded_at__";
// If a "stale chunk" error fires within this window AFTER we already
// did a reload, the reload didn't help — the problem isn't a stale
// bundle, it's something else (service worker stuck on old cache,
// CDN propagation delay, real bug). Don't reload again; surface it.
const RELOAD_GRACE_MS = 60_000;

export function lazyRetry(factory) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const isChunkError =
        err?.name === "ChunkLoadError" ||
        /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
          err?.message || ""
        );

      if (!isChunkError) throw err;

      let alreadyReloadedRecently = false;
      try {
        if (typeof sessionStorage !== "undefined") {
          const wasReloaded = sessionStorage.getItem(RELOAD_FLAG_KEY) === "1";
          const reloadedAt = Number(sessionStorage.getItem(RELOAD_FLAG_TS_KEY) || 0);
          alreadyReloadedRecently =
            wasReloaded && reloadedAt && Date.now() - reloadedAt < RELOAD_GRACE_MS;
        }
      } catch {
        // sessionStorage disabled (private mode etc.) — fall through and
        // try to reload. We accept the small risk of a loop over the
        // larger risk of leaving the user on a broken page.
      }

      if (alreadyReloadedRecently) {
        // We already reloaded this session and it didn't fix the chunk
        // error within the grace window. Either the SW is serving a
        // stale cache (separate fix — see public/sw.js) or this is a
        // real bug. Surface to ErrorBoundary + Sentry.
        throw err;
      }

      try {
        sessionStorage.setItem(RELOAD_FLAG_KEY, "1");
        sessionStorage.setItem(RELOAD_FLAG_TS_KEY, String(Date.now()));
      } catch {}

      // Try to wipe any cached app shell that might be serving stale
      // chunk references, then unregister the SW so the reload pulls
      // a totally fresh tree. Best-effort; if any of this throws we
      // just fall through to the reload.
      try {
        if (typeof caches !== "undefined") {
          const names = await caches.keys();
          await Promise.all(names.map((n) => caches.delete(n)));
        }
      } catch {}
      try {
        if (typeof navigator !== "undefined" && navigator.serviceWorker) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
      } catch {}

      // Force-bypass the browser HTTP cache by appending a unique
      // query param. window.location.reload() honors the cached
      // `index.html` AND the cached main chunk (which is marked
      // immutable, max-age=1yr). For users with a stale main
      // chunk in HTTP cache, .reload() returns the same dead
      // chunk references and lazyRetry loops itself out within
      // the 60s grace window, leaving the user on the
      // ErrorBoundary fallback. Appending ?_v=<ts> guarantees
      // both the index.html AND the main chunk URLs become
      // novel — browser MUST hit the network, picking up the
      // current build with current chunk hashes.
      const u = new URL(window.location.href);
      u.searchParams.set("_v", String(Date.now()));
      window.location.assign(u.toString());

      // Return a promise that never resolves so React doesn't render
      // anything from this lazy import while the browser is reloading.
      return new Promise(() => {});
    }
  });
}

export default lazyRetry;
